import React, { useState, useEffect } from 'react';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Save, RefreshCw } from 'lucide-react';
import { useToast } from '../hooks/use-toast';
import api from '../services/api';
import AdminNav from '../components/AdminNav';

const RewardAdminPage = () => {
  const [rewards, setRewards] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [typeChanges, setTypeChanges] = useState({});
  const [monthChanges, setMonthChanges] = useState({});
  const { toast } = useToast();

  useEffect(() => {
    // Ensure API token is set for parent authentication
    const token = localStorage.getItem('access_token');
    if (token) {
      api.setToken(token);
    }
    loadRewards();
  }, []);

  const loadRewards = async () => {
    try {
      setLoading(true);
      console.log('Loading rewards...');
      
      // Try multiple token sources
      let token = localStorage.getItem('access_token');
      console.log('Token from localStorage:', token ? token.substring(0, 20) + '...' : 'null');
      
      if (!token) {
        console.log('No token found, redirecting to login...');
        window.location.href = '/login';
        return;
      }
      
      api.setToken(token);
      
      // Make direct fetch call to debug
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL || 'http://localhost:8001'}/api/rewards/items`, {
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        }
      });
      
      console.log('API response status:', response.status);
      console.log('API response ok:', response.ok);
      
      if (!response.ok) {
        throw new Error(`API returned ${response.status}: ${response.statusText}`);
      }
      
      const responseText = await response.text();
      console.log('Raw response length:', responseText.length);
      console.log('Raw response preview:', responseText.substring(0, 200));
      
      const data = JSON.parse(responseText);
      console.log('Parsed data length:', data?.length);
      console.log('Parsed data type:', typeof data);
      console.log('First reward:', data?.[0]?.name);
      
      if (!Array.isArray(data)) {
        throw new Error(`Expected array, got ${typeof data}`);
      }
      
      // Sort alphabetically
      data.sort((a, b) => a.name.localeCompare(b.name));
      setRewards(data);
      setTypeChanges({});
      setMonthChanges({});
    } catch (error) {
      console.error('Error loading rewards:', error);
      console.error('Error details:', error.message);
      toast({
        title: 'Error',
        description: `Failed to load rewards: ${error.message}`,
        variant: 'destructive'
      });
    } finally {
      setLoading(false);
    }
  };

  const handleTypeChange = (rewardId, newType) => {
    setTypeChanges(prev => ({
      ...prev,
      [rewardId]: newType
    }));
  };

  const handleMonthChange = (rewardId, newMonth) => {
    setMonthChanges(prev => ({
      ...prev,
      [rewardId]: newMonth
    }));
  };

  const saveChanges = async () => {
    const totalChanges = Object.keys(typeChanges).length + Object.keys(monthChanges).length;
    
    if (totalChanges === 0) {
      toast({
        title: 'No Changes',
        description: 'No changes have been made',
      });
      return;
    }

    try {
      setSaving(true);
      
      // Save type changes
      for (const [rewardId, newType] of Object.entries(typeChanges)) {
        await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/rewards/items/${rewardId}/type`, {
          method: 'PUT',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${localStorage.getItem('access_token')}`
          },
          body: JSON.stringify({ reward_type: newType })
        });
      }

      // Save month changes
      for (const [rewardId, newMonth] of Object.entries(monthChanges)) {
        await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/rewards/items/${rewardId}/month`, {
          method: 'PUT',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${localStorage.getItem('access_token')}`
          },
          body: JSON.stringify({ month: newMonth })
        });
      }

      toast({
        title: 'Success!',
        description: `Updated ${totalChanges} change(s)`,
      });

      // Reload rewards
      await loadRewards();
    } catch (error) {
      console.error('Error saving changes:', error);
      toast({
        title: 'Error',
        description: 'Failed to save changes',
        variant: 'destructive'
      });
    } finally {
      setSaving(false);
    }
  };

  const getCurrentType = (reward) => {
    return typeChanges[reward.id] || reward.reward_type;
  };

  const getCurrentMonth = (reward) => {
    return monthChanges[reward.id] || reward.month || 'Not set';
  };

  const hasChanges = Object.keys(typeChanges).length > 0 || Object.keys(monthChanges).length > 0;
  const totalChanges = Object.keys(typeChanges).length + Object.keys(monthChanges).length;

  const stickers = rewards.filter(r => getCurrentType(r) === 'sticker');
  const pets = rewards.filter(r => getCurrentType(r) === 'pet');

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <p className="text-xl text-slate-600">Loading rewards...</p>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50">
      <AdminNav />
      <div className="max-w-7xl mx-auto p-6">
        <div className="mb-6 flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold text-slate-900">Reward Type Manager</h1>
            <p className="text-slate-600 mt-2">
              Fix reward types - Total: {rewards.length} ({stickers.length} stickers, {pets.length} pets)
            </p>
          </div>
          <div className="flex gap-3">
            <Button
              onClick={loadRewards}
              variant="outline"
              disabled={saving}
            >
              <RefreshCw className="w-4 h-4 mr-2" />
              Refresh
            </Button>
            <Button
              onClick={saveChanges}
              disabled={!hasChanges || saving}
              className="bg-green-600 hover:bg-green-700"
            >
              <Save className="w-4 h-4 mr-2" />
              Save {hasChanges ? `(${totalChanges})` : ''}
            </Button>
          </div>
        </div>

        {hasChanges && (
          <Card className="p-4 mb-6 bg-yellow-50 border-yellow-200">
            <p className="text-yellow-800 font-medium">
              ⚠️ You have {totalChanges} unsaved change(s). Click "Save" to apply them.
            </p>
          </Card>
        )}

        <div className="grid grid-cols-1 gap-4">
          {rewards.map((reward) => {
            const currentType = getCurrentType(reward);
            const currentMonth = getCurrentMonth(reward);
            const isTypeChanged = typeChanges[reward.id] !== undefined;
            const isMonthChanged = monthChanges[reward.id] !== undefined;
            const isChanged = isTypeChanged || isMonthChanged;

            return (
              <Card key={reward.id} className={`p-4 ${isChanged ? 'ring-2 ring-blue-500' : ''}`}>
                <div className="flex items-center gap-4">
                  {/* Image */}
                  <div className="w-20 h-20 rounded-lg overflow-hidden border-2 border-slate-200 flex-shrink-0 bg-white">
                    <img 
                      src={reward.image_url || reward.icon} 
                      alt={reward.name}
                      className="w-full h-full object-cover"
                    />
                  </div>

                  {/* Name */}
                  <div className="flex-1">
                    <h3 className="font-semibold text-lg text-slate-900">{reward.name}</h3>
                    <p className="text-sm text-slate-600">Cost: {reward.cost} tokens</p>
                  </div>

                  {/* Type Selector */}
                  <div className="flex items-center gap-3">
                    <div className="flex flex-col gap-1">
                      <label className="text-xs text-slate-600 font-medium">Type</label>
                      <Select
                        value={currentType}
                        onValueChange={(value) => handleTypeChange(reward.id, value)}
                      >
                        <SelectTrigger className="w-32">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="sticker">🎨 Sticker</SelectItem>
                          <SelectItem value="pet">🐾 Pet</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>

                    {/* Month Selector */}
                    <div className="flex flex-col gap-1">
                      <label className="text-xs text-slate-600 font-medium">Month</label>
                      <Select
                        value={currentMonth}
                        onValueChange={(value) => handleMonthChange(reward.id, value)}
                      >
                        <SelectTrigger className="w-36">
                          <SelectValue placeholder="Select month" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="January">January</SelectItem>
                          <SelectItem value="February">February</SelectItem>
                          <SelectItem value="March">March</SelectItem>
                          <SelectItem value="April">April</SelectItem>
                          <SelectItem value="May">May</SelectItem>
                          <SelectItem value="June">June</SelectItem>
                          <SelectItem value="July">July</SelectItem>
                          <SelectItem value="August">August</SelectItem>
                          <SelectItem value="September">September</SelectItem>
                          <SelectItem value="October">October</SelectItem>
                          <SelectItem value="November">November</SelectItem>
                          <SelectItem value="December">December</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>

                    {isChanged && (
                      <div className="flex flex-col gap-1">
                        <div className="h-4"></div> {/* Spacer to align with inputs */}
                        <Badge variant="outline" className="bg-blue-50 text-blue-700 border-blue-300">
                          Changed
                        </Badge>
                      </div>
                    )}
                  </div>
                </div>
              </Card>
            );
          })}
        </div>
      </div>
    </div>
  );
};

export default RewardAdminPage;
